import {
  AfterViewInit,
  Component,
  computed,
  Signal,
  TemplateRef,
  ViewChild,
  inject,
  signal,
  OnInit,
} from '@angular/core';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { Store } from '@ngxs/store';
import { SetBusinessStateFromPaymentComplete, UpdatePageTitle } from '../../../store/app/app.actions';
import { TableColumn } from '@swimlane/ngx-datatable';
import { Observable } from 'rxjs';
import { AppTableComponent } from '../../../shared/components/table/app-table.component';
import { noAccountingTransactionTableColumns } from '../accounting/transactions/_utils/transactions-utils';
import { AccountBalanceWidgetComponent } from './widgets/account-balance-widget/account-balance-widget.component';
import { AverageSpendWidgetComponent } from './widgets/average-spend-widget/average-spend-widget.component';
import { CardsWidgetComponent } from './widgets/cards-widget/cards-widget.component';
import { HealthStatusWidgetComponent } from './widgets/health-status-widget/health-status-widget.component';
import { AccountingSoftwareWidgetComponent } from './widgets/accounting-software-widget/accounting-software-widget.component';
import { Transaction } from '../../../_interfaces/transactions.interface';
import { HttpParams } from '@angular/common/http';
import { PaginatedResults } from '@bo/ng-api';
import { TransactionsApiService } from '../../../_services/transactions-service/transactions-api.service';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { AlertKycStatusComponent } from '../../../shared/components/alert-kyc-status/alert-kyc-status.component';
import { ToastService } from '../../../_services/toast-service/toast.service';
import { idStatus } from '../../../shared/utils/general-utils';
import { helpCenterUrl } from '../../../_fixtures/project-fixtures';
import { EmptyIndicatorPipe } from '../../../shared/pipes/empty-indicator.pipe';

@Component({
  selector: 'app-dashboard',
  imports: [
    HealthStatusWidgetComponent,
    CardsWidgetComponent,
    AverageSpendWidgetComponent,
    AccountBalanceWidgetComponent,
    AccountingSoftwareWidgetComponent,
    AppTableComponent,
    DatePipe,
    RouterLink,
    CurrencyPipe,
    AlertKycStatusComponent,
    EmptyIndicatorPipe,
  ],
  templateUrl: './dashboard.component.html',
})
export class DashboardComponent implements OnInit, AfterViewInit {
  readonly store = inject(Store);
  readonly transactionsApiService = inject(TransactionsApiService);
  readonly route = inject(ActivatedRoute);
  readonly toastService = inject(ToastService);

  /* eslint-disable @typescript-eslint/no-explicit-any */
  // TODO: TemplateRef<any> can be fixed by refactoring the @bo/ng-datatable library.
  @ViewChild('dateCell', { static: false }) dateCell!: TemplateRef<any>;
  @ViewChild('merchantCell', { static: false }) merchantCell!: TemplateRef<any>;
  @ViewChild('categoryCell', { static: false }) categoryCell!: TemplateRef<any>;
  @ViewChild('amountCell', { static: false }) amountCell!: TemplateRef<any>;
  @ViewChild('receiptCell', { static: false }) receiptCell!: TemplateRef<any>;
  /* eslint-enable @typescript-eslint/no-explicit-any */

  protected columns: Array<TableColumn> = [];
  showFundAccountAlert = false;
  private readonly columnsInitialized = signal(false);

  protected payment: Signal<string> = computed(() => {
    const queryParams = this.route.snapshot.queryParams;
    return queryParams['payment'] as string;
  });

  constructor() {
    this.store.dispatch(new UpdatePageTitle('Dashboard'));
    if (this.payment() === 'success') {
      this.toastService.show(`Your subscription has been activated.`, 'iconoir-check-circle');
      this.store.dispatch(new SetBusinessStateFromPaymentComplete());
    }
    if (this.payment() === 'error')
      this.toastService.show(`Your subscription payment has failed. Please try again. `, 'iconoir-xmark');
  }

  fundAccountAlert(event: boolean) {
    this.showFundAccountAlert = event;
  }

  ngOnInit() {
    // Initialise columns early to prevent expression changed error
    // Copy each column so cell templates aren't written onto the shared constant
    this.columns = noAccountingTransactionTableColumns.map((column) => ({ ...column }));
  }

  ngAfterViewInit(): void {
    this.setupColumns();
  }

  private setupColumns(): void {
    for (const column of this.columns) {
      if (column.prop === 'timestamp') column.cellTemplate = this.dateCell;
      if (column.prop === 'merchant') column.cellTemplate = this.merchantCell;
      if (column.prop === 'category') column.cellTemplate = this.categoryCell;
      if (column.prop === 'amount') column.cellTemplate = this.amountCell;
      if (column.prop === 'receipt_status') column.cellTemplate = this.receiptCell;
    }
    // Reassign array to trigger change detection in the table
    this.columns = [...this.columns];
    this.columnsInitialized.set(true);
  }

  getData(queryParams: HttpParams): Observable<PaginatedResults<Transaction>> {
    return this.transactionsApiService.getTransactions(queryParams);
  }

  protected readonly idStatus = idStatus;
  protected readonly helpCenterUrl = helpCenterUrl;
}
